import Big from 'big.js';
import { useCallback, useContext, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerOperationArchiveButton } from '@/modules/issuer-dashboard/components/IssuerOperationArchiveButton.tsx';
import { IssuerActivitiesOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerActivitiesOperationInfoModal.tsx';
import { IssuerTransactionsOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { useTransactionArchiveByIdMutation } from '@/modules/issuer-dashboard/queries/transactions.ts';
import { DisclosureListItem, ModalProviderContext } from 'ui';
import { fixNameEnum, goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';
import { TransactionPaginateResponse, TransactionType } from 'api';

interface IssuerHolderTransactionTabItemProps {
  transaction: TransactionPaginateResponse;
  isDeleted?: boolean;
  showArchiveButton?: boolean;
}

const IssuerHolderTransactionTabItem = ({
  transaction,
  isDeleted = false,
  showArchiveButton = false,
}: IssuerHolderTransactionTabItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const {
    id,
    type,
    memo,
    payment_id,
    issue_date,
    register_class,
    price_per_share,
    requested_at_date,
    quantity,
    dividend_event_id,
    register_id,
  } = transaction;

  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { mutateAsync: archiveTransaction } = useTransactionArchiveByIdMutation();

  const handleOpenFullTransaction = useCallback(() => {
    if (!id) return;
    openModal(IssuerTransactionsOperationInfoModal, {
      id,
      registerId,
      registerCountry,
      isDeleted,
    });
  }, [id, isDeleted, openModal, registerCountry, registerId]);

  const handleOpenFullPayment = useCallback(() => {
    if (!payment_id) return;
    openModal(IssuerActivitiesOperationInfoModal, {
      paymentId: payment_id,
      registerId,
      registerCountry,
      isDeleted,
    });
  }, [isDeleted, openModal, payment_id, registerCountry, registerId]);

  const bottomContentData = useMemo<Array<[JSX.Element | string, JSX.Element | string]>>(() => {
    const info: Array<[JSX.Element | string, JSX.Element | string]> = [];

    if (issue_date) info.push(['Completion date', dateToPrint(issue_date) || '-']);
    if (requested_at_date) info.push(['Request date', dateToPrint(requested_at_date) || '-']);
    if (price_per_share)
      info.push(['Price per share/unit', goodFormatUSDCurrency(price_per_share).toString()]);

    if (register_id) info.push(['Register ID', `#${register_id}`]);

    if (payment_id) {
      info.push([
        'Payment ID',
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullPayment}
        >
          #{payment_id}
        </span>,
      ]);
    }
    if (id) {
      info.push([
        'Transaction ID',
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullTransaction}
        >
          #{id}
        </span>,
      ]);
    }
    if (dividend_event_id) {
      info.push([
        `${getRegisterTypeStrings('dividend', { capitalize: true })} event ID`,
        `#${dividend_event_id}`,
      ]);
    }
    if (memo) info.push(['Memo', memo]);
    return info;
  }, [
    requested_at_date,
    issue_date,
    price_per_share,
    register_id,
    payment_id,
    id,
    dividend_event_id,
    memo,
    getRegisterTypeStrings,
    handleOpenFullPayment,
    handleOpenFullTransaction,
  ]);

  const name = useMemo(() => {
    if (type === TransactionType.Subscription) return getRegisterTypeStrings('application');
    if (type === TransactionType.Drp) return 'Dividend reinvestment';
    return fixNameEnum(type);
  }, [getRegisterTypeStrings, type]);
  return (
    <DisclosureListItem
      leftMainString={name}
      rightMainString={goodFormatUSDCurrency(
        +new Big(quantity ?? 0)?.mul(price_per_share ?? 0)?.valueOf(),
      )}
      leftSubduedContentSet={[
        issue_date ? dateToPrint(issue_date) : 'No completion date',
        register_class?.class_name || '-',
      ]}
      rightSubduedString={`${goodFormatNumber(quantity ?? 0)} units/shares`}
      classes={{
        rightMainString: 'max-w-[200px] min-w-[140px]',
        rightContent: !isDeleted && showArchiveButton && id ? 'gap-1 w-[60px]' : undefined,
      }}
      rightContent={<ActivityStatusBadge status={transaction.status || ''} />}
      rightAdditionalContent={
        !isDeleted && showArchiveButton && id ? (
          <IssuerOperationArchiveButton
            id={id}
            entityName="transaction"
            onArchive={archiveTransaction}
          />
        ) : null
      }
      bottomContent={bottomContentData}
    />
  );
};

export default IssuerHolderTransactionTabItem;
